/* eslint-disable no-plusplus */
/* eslint-disable no-mixed-operators */
import { useCallback, useRef, useState } from 'react';
import * as PDFJS from 'pdfjs-dist/webpack.mjs';
import { pdfjsAssetOptions } from 'services/pdfjs/assets';
import { logger } from 'utils/logger';

export type ProcessedImage = {
	id: string;
	original: string;
	preview: string;
	postProcessed: string;
	selected: boolean;
	label: string;
	sendAs: "Text" | "Image"
};

export type DocumentAttachmentState = {
	fileName: string | null;
	isProcessing: boolean;
	progress: number;
	statusText: string;
	images: ProcessedImage[];
	fileType: 'image' | 'pdf' | null;
};

type UseDocumentAttachmentReturn = {
	state: DocumentAttachmentState;
	processFile: (file: File) => Promise<void>;
	removeFile: () => void;
	toggleSelection: (id: string) => void;
	selectAll: (selected: boolean) => void;
	getSelectedImages: () => ProcessedImage[];
	updateSendAs: (id: string, sendAs: ProcessedImage['sendAs']) => void;
};

const PREVIEW_MAX_WIDTH = 150;
const PDF_SCALE_FACTOR = 2;
const POST_PROCESS_THRESHOLD = 225;
const POST_PROCESS_POSTERIZE_LEVELS = 3;
const POST_PROCESS_MAX_WIDTH = 1200;
const POST_PROCESS_JPEG_QUALITY = 0.5;

const loadImage = (src: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = src;
  });
};

const generatePreview = async (originalDataUrl: string): Promise<string> => {
  const img = await loadImage(originalDataUrl);
  const scale = img.width > PREVIEW_MAX_WIDTH ? PREVIEW_MAX_WIDTH / img.width : 1;
  const width = Math.round(img.width * scale);
  const height = Math.round(img.height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(img, 0, 0, width, height);

  return canvas.toDataURL('image/png');
};

const postProcess = async (originalDataUrl: string): Promise<string> => {
  const img = await loadImage(originalDataUrl);

  const scale = img.width > POST_PROCESS_MAX_WIDTH ? POST_PROCESS_MAX_WIDTH / img.width : 1;
  const width = Math.round(img.width * scale);
  const height = Math.round(img.height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(img, 0, 0, width, height);

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const { data } = imageData;

  const threshold = POST_PROCESS_THRESHOLD;
  const levels = POST_PROCESS_POSTERIZE_LEVELS;

  for (let i = 0; i < data.length; i += 4) {
    const gray = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;

    let value: number;
    if (gray >= threshold) {
      value = 255;
    } else {
      value = Math.round(Math.round((gray / 255) * (levels - 1)) * (255 / (levels - 1)));
    }

    data[i] = value;
    data[i + 1] = value;
    data[i + 2] = value;
  }

  ctx.putImageData(imageData, 0, 0);
  return canvas.toDataURL('image/jpeg', POST_PROCESS_JPEG_QUALITY);
};

export const useDocumentAttachment = (): UseDocumentAttachmentReturn => {
  const [state, setState] = useState<DocumentAttachmentState>({
    fileName: null,
    isProcessing: false,
    progress: 0,
    statusText: '',
    images: [],
    fileType: null,
  });

  const abortRef = useRef(false);

  const updateProgress = (progress: number, statusText: string) => {
    setState((prev) => ({ ...prev, progress, statusText }));
  };

  const processImage = async (file: File): Promise<void> => {
    updateProgress(10, 'Reading image...');

    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });

    if (abortRef.current) return;

    updateProgress(30, 'Generating preview...');
    const preview = await generatePreview(dataUrl);

    if (abortRef.current) return;

    updateProgress(60, 'Post-processing image...');
    const postProcessed = await postProcess(dataUrl);

    if (abortRef.current) return;

    updateProgress(100, 'Done');

    const processedImage: ProcessedImage = {
      id: `img-0`,
      original: dataUrl,
      preview,
      postProcessed,
      selected: true,
      label: file.name,
      sendAs: "Text"
    };

    setState((prev) => ({
      ...prev,
      isProcessing: false,
      progress: 100,
      statusText: 'Done',
      images: [processedImage],
      fileType: 'image',
      fileName: file.name,
    }));
  };

  const processPdf = async (file: File): Promise<void> => {
    updateProgress(5, 'Loading PDF...');

    const data = new Uint8Array(await file.arrayBuffer());

    const pdf = await PDFJS.getDocument({ data, ...pdfjsAssetOptions }).promise;
    const { numPages } = pdf;

    if (abortRef.current) return;

    const processedImages: ProcessedImage[] = [];

    for (let pageNum = 1; pageNum <= numPages; pageNum++) {
      if (abortRef.current) return;

      const baseProgress = Math.round((pageNum - 1) / numPages * 80) + 10;
      updateProgress(baseProgress, `Processing page ${pageNum} of ${numPages}...`);

      const page = await pdf.getPage(pageNum);
      const viewport = page.getViewport({ scale: PDF_SCALE_FACTOR });

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d')!;
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      await page.render({ canvasContext: context, viewport }).promise;

      const originalDataUrl = canvas.toDataURL('image/png');

      if (abortRef.current) return;

      const preview = await generatePreview(originalDataUrl);

      if (abortRef.current) return;

      const postProcessedUrl = await postProcess(originalDataUrl);

      if (abortRef.current) return;

      processedImages.push({
        id: `page-${pageNum}`,
        original: originalDataUrl,
        preview,
        postProcessed: postProcessedUrl,
        selected: false,
        label: `Page ${pageNum}`,
        sendAs: "Text"
      });
    }

    updateProgress(100, 'Done');

    setState((prev) => ({
      ...prev,
      isProcessing: false,
      progress: 100,
      statusText: 'Done',
      images: processedImages,
      fileType: 'pdf',
      fileName: file.name,
    }));
  };

  const processFile = async (file: File) => {
    abortRef.current = false;

    setState({
      fileName: file.name,
      isProcessing: true,
      progress: 0,
      statusText: 'Starting...',
      images: [],
      fileType: null,
    });

    try {
      if (file.type === 'application/pdf') {
        await processPdf(file);
      } else {
        await processImage(file);
      }
    } catch (error) {
      logger.error('Error processing file:', error);
      setState((prev) => ({
        ...prev,
        isProcessing: false,
        progress: 0,
        statusText: 'Error processing file',
      }));
    }
  };

  const removeFile = useCallback(() => {
    abortRef.current = true;
    setState({
      fileName: null,
      isProcessing: false,
      progress: 0,
      statusText: '',
      images: [],
      fileType: null,
    });
  }, []);

  const toggleSelection = useCallback((id: string) => {
    setState((prev) => ({
      ...prev,
      images: prev.images.map((img) =>
        img.id === id ? { ...img, selected: !img.selected } : img
      ),
    }));
  }, []);

  const selectAll = useCallback((selected: boolean) => {
    setState((prev) => ({
      ...prev,
      images: prev.images.map((img) => ({ ...img, selected })),
    }));
  }, []);

  const getSelectedImages = useCallback((): ProcessedImage[] => {
    return state.images
      .filter((img) => img.selected);
  }, [state.images]);

  const updateSendAs = useCallback((id: string, sendAs: ProcessedImage['sendAs']) => {
    setState((prev) => ({
      ...prev,
      images: prev.images.map((img) =>
        img.id === id ? { ...img, sendAs } : img
      ),
    }));
  }, []);

  return {
    state,
    processFile,
    removeFile,
    toggleSelection,
    selectAll,
    getSelectedImages,
    updateSendAs,
  };
};
